/**
 * `/join#<token>` — where an invitation link lands, and now where it is ACCEPTED.
 *
 * ## What this screen used to be, and why that was a dead end
 *
 * One sentence and one button: "Create an account. Use the email address the invitation was sent
 * to." It could not name that address, because nothing would tell a signed-out browser what it
 * was, so it asked people to remember it and sent them to a blank signup form. Three things went
 * wrong with that, and the founder hit all three on 2026-09-12:
 *
 *   1. **Guessing the address wrong is unrecoverable.** `accept_org_invitation` binds acceptance to
 *      the invited address, so an account created under a different one can never redeem the link
 *      it was made for — and its owner finds that out after choosing a password.
 *   2. **Somebody who already has an account had no door at all.** The only button made a new one,
 *      and Supabase answers a signup for an existing address with its anti-enumeration response —
 *      no error, no session — which the signup screen rendered as "Check your email. Open it, then
 *      sign in." Nothing was ever sent, so that is a wait with no end. Both doors are here now.
 *   3. **It collected no name.** Somebody joining a colleague's organization is introducing
 *      themselves to people who will see their name on an envelope, and the profile wizard is
 *      behind the sign-in they have not completed yet.
 *
 * So the screen reads the invitation first (`POST /v1/invitations/preview`), shows the address it
 * is FOR as a locked field, and collects a first name, a last name, a display name and a password
 * in one form. Submitting does the whole of it: create the account, redeem the invitation, save the
 * profile, land in the organization.
 *
 * ## The token is in the fragment and stays out of everything else
 *
 * A fragment is not sent to the server, does not appear in access logs, and is not included in
 * `Referer` on the next navigation. It is read once into state and cleared from the address bar
 * immediately, so the link does not sit in the URL of a page somebody may later screenshot or leave
 * open — and when it goes to the API it goes in a POST body rather than a path, for the same reason.
 *
 * Clearing it has a cost worth naming: a reload after clearing loses the token and the person has
 * to click the original link again. That is the correct trade against a live credential parked in
 * the address bar of a shared machine.
 *
 * ## What this screen will not say
 *
 * Whether the invited address already has an account. An administrator can mint an invitation for
 * any address, so an answer here would be an account-enumeration oracle costing one invitation
 * (security.md §11). Both doors are offered and neither claims to be the right one — the person
 * holding the link already knows which of the two they are.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Callout, Card, Field, LoadingState } from '@flowza/ui'
import { ApiError, api } from '../../api/client'
import { previewInvitation, roleLabel, type InvitationPreview } from '../../api/invitations'
import { describeUnlessNotDeployed } from '../app/apiErrors'
import { getClient } from '../../supabase'
import {
  PASSWORD_HINT,
  TOO_SHORT_MESSAGE,
  tooShort as isTooShort,
  weakPasswordMessage,
} from '../../auth/password-failure'
import { AuthLayout } from './AuthLayout'

/** The cell's label, sent only so a development cell that declares none has a value to accept. */
const CELL_REGION: string | undefined =
  typeof import.meta.env.VITE_CELL_REGION === 'string' && import.meta.env.VITE_CELL_REGION !== ''
    ? (import.meta.env.VITE_CELL_REGION as string)
    : undefined

export type JoinState =
  | { readonly kind: 'reading' }
  | { readonly kind: 'no_token' }
  /** The link resolved. `signedInAs` is this browser's session, when there is one. */
  | {
      readonly kind: 'ready'
      readonly invitation: InvitationPreview
      readonly signedInAs: string | null
    }
  | { readonly kind: 'working'; readonly note: string }
  | { readonly kind: 'joined'; readonly organizationName: string }
  | { readonly kind: 'already_member'; readonly organizationName: string }
  /** Expired, withdrawn, already used, never issued — one answer, the API's own. */
  | { readonly kind: 'not_open' }
  /** The definer refused. Its own words, because they are chosen to leak nothing. */
  | { readonly kind: 'refused'; readonly message: string }
  /** Signed up, and the cell wants the address confirmed before it will issue a session. */
  | { readonly kind: 'confirm'; readonly email: string }
  | { readonly kind: 'error'; readonly message: string; readonly retry: () => void }

/**
 * Reads the token out of the fragment.
 *
 * Exported for its test: this is the one function whose failure mode is "a live credential is left
 * in the address bar", which no rendering assertion would notice.
 */
export function takeTokenFromFragment(location: {
  hash: string
  pathname: string
  search: string
}): string | null {
  const raw = location.hash.replace(/^#/, '')
  if (raw.length === 0) return null
  let token: string
  try {
    token = decodeURIComponent(raw)
  } catch {
    // A malformed escape is not a token. Returning null sends the user down the "this link is not
    // complete" path rather than presenting a mangled value to the API.
    return null
  }
  return token.trim().length === 0 ? null : token
}

/** "Ada Lovelace" from the two boxes above it, until the person edits it themselves. */
export function suggestedDisplayName(first: string, last: string): string {
  return `${first.trim()} ${last.trim()}`.trim()
}

export type JoinSubmission = {
  readonly firstName: string
  readonly lastName: string
  readonly displayName: string
  readonly password: string
}

export function JoinView({
  state,
  onCreate,
  onSignIn,
  onAcceptAsSignedIn,
  onSignOut,
  failure,
}: {
  readonly state: JoinState
  readonly onCreate: (input: JoinSubmission) => void
  readonly onSignIn: (password: string) => void
  /** Already signed in as the invited address: no form, one button. */
  readonly onAcceptAsSignedIn: () => void
  readonly onSignOut: () => void
  /** Whatever the last attempt came back with, shown where the input that has to change is. */
  readonly failure?: { readonly field: 'password' | 'form'; readonly message: string } | undefined
}) {
  const [mode, setMode] = useState<'create' | 'signin'>('create')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [displayNameEdited, setDisplayNameEdited] = useState(false)
  const [password, setPassword] = useState('')

  const busy = state.kind === 'working'
  const tooShort = isTooShort(password)
  const passwordError =
    failure?.field === 'password'
      ? failure.message
      : tooShort && password !== ''
        ? TOO_SHORT_MESSAGE
        : null

  if (state.kind === 'reading') return <LoadingState label="Reading your invitation" />

  if (state.kind === 'no_token') {
    return (
      <Shell title="This invitation link is not complete">
        <p className="text-text-muted mb-0 mt-2 text-sm">
          The part after the <code>#</code> is missing. Invitation links break this way when they
          are copied out of a message by hand — ask whoever invited you to send the whole link, or
          to make you a new one.
        </p>
      </Shell>
    )
  }

  if (state.kind === 'not_open') {
    return (
      <Shell title="This invitation cannot be used">
        <Callout tone="warning" title="This link will not open">
          Invitations last seven days, can be withdrawn by the person who sent them, and can only be
          used once. Ask them to send you a new one.
        </Callout>
      </Shell>
    )
  }

  if (state.kind === 'refused') {
    return (
      <Shell title="This invitation cannot be used">
        {/* The API's own sentence: it is chosen to leak nothing about invitations that are not
            the caller's, so it is shown rather than reworded. */}
        <Callout tone="warning" title={state.message}>
          An invitation only works for the address it names, and only for somebody who does not
          already have a FlowZa organization of their own.
        </Callout>
      </Shell>
    )
  }

  if (state.kind === 'joined') {
    return (
      <Shell title={`You have joined ${state.organizationName}`}>
        <p className="text-text-muted mb-0 mt-2 text-sm">Taking you to your documents…</p>
      </Shell>
    )
  }

  if (state.kind === 'already_member') {
    return (
      <Shell title={`You are already in ${state.organizationName}`}>
        <p className="text-text-muted mb-0 mt-2 text-sm">
          Nothing to do — this invitation had already been used by your account.
        </p>
      </Shell>
    )
  }

  if (state.kind === 'confirm') {
    return (
      <Shell title="One more step">
        {/*
          BOTH branches, because this screen cannot tell which happened and must not pretend.
          Supabase answers a signup for an address that already has an account with exactly the
          same shape as one for a new address — no error, no session — deliberately, so that a
          signup form cannot be used to discover who has an account. Saying only "check your
          email" is what left the founder waiting for a message that was never sent.
        */}
        <Callout tone="info" title="Check your email, or sign in">
          If {state.email} is new to FlowZa, we sent it a confirmation link — open it, then come
          back to this invitation. If that address already has an account, nothing was sent: sign in
          with it instead, and this invitation will be waiting.
        </Callout>
        <p className="text-text-muted mb-0 mt-3 text-sm">
          You will need this link again either way, so keep the message it came in.
        </p>
      </Shell>
    )
  }

  if (state.kind === 'error') {
    return (
      <Shell title="Something went wrong">
        <p className="text-danger mt-2 text-sm" role="alert">
          {state.message}
        </p>
        <Button onClick={state.retry}>Try again</Button>
      </Shell>
    )
  }

  const invitation = state.kind === 'working' ? null : state.invitation
  const signedInAs = state.kind === 'working' ? null : state.signedInAs
  const invitedEmail = invitation?.email ?? ''
  const sameAccount = signedInAs !== null && signedInAs.toLowerCase() === invitedEmail.toLowerCase()

  return (
    <Shell
      title={invitation === null ? 'Joining…' : `Join ${invitation.organizationName}`}
      subtitle={
        invitation === null ? undefined : (
          <>
            You were invited as <strong>{roleLabel(invitation.role)}</strong>.
          </>
        )
      }
    >
      {busy ? <LoadingState label={state.kind === 'working' ? state.note : 'Working'} /> : null}

      {invitation === null ? null : (
        <>
          {/*
            The invited address, and it is not a text box. Acceptance binds to it, so offering it
            as something to edit would be offering a way to make an account that can never redeem
            this link.
          */}
          <Field
            label="Your email address"
            type="email"
            value={invitation.email}
            readOnly
            disabled
            hint="The invitation was sent to this address, and only this address can accept it."
          />

          {signedInAs !== null && !sameAccount ? (
            <div className="mt-4">
              <Callout tone="warning" title="You are signed in with another account">
                This invitation is for {invitation.email} and you are signed in as {signedInAs}.
                Sign out and use the invited address.
              </Callout>
              <div className="mt-3">
                <Button variant="secondary" onClick={onSignOut} disabled={busy}>
                  Sign out
                </Button>
              </div>
            </div>
          ) : sameAccount ? (
            <div className="mt-4">
              <p className="text-text-muted m-0 text-sm">
                You are signed in as {signedInAs}, which is who this invitation is for.
              </p>
              {failure === undefined ? null : (
                <p className="text-danger mt-2 text-sm" role="alert">
                  {failure.message}
                </p>
              )}
              <div className="mt-3">
                <Button onClick={onAcceptAsSignedIn} disabled={busy}>
                  {busy ? 'Joining…' : `Join ${invitation.organizationName}`}
                </Button>
              </div>
            </div>
          ) : mode === 'create' ? (
            <form
              className="mt-4 flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault()
                onCreate({
                  firstName: firstName.trim(),
                  lastName: lastName.trim(),
                  displayName:
                    displayName.trim() === ''
                      ? suggestedDisplayName(firstName, lastName)
                      : displayName.trim(),
                  password,
                })
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="First name"
                  name="first_name"
                  autoComplete="given-name"
                  required
                  maxLength={80}
                  disabled={busy}
                  value={firstName}
                  onChange={(e) => setFirstName(e.currentTarget.value)}
                />
                <Field
                  label="Last name"
                  name="last_name"
                  autoComplete="family-name"
                  required
                  maxLength={80}
                  disabled={busy}
                  value={lastName}
                  onChange={(e) => setLastName(e.currentTarget.value)}
                />
              </div>
              {/*
                Filled from the two boxes above until somebody types here, and left alone from then
                on. This is the name colleagues see on an envelope, so it is offered rather than
                derived silently — a person who goes by something other than their legal first name
                should not have to delete a guess to say so.
              */}
              <Field
                label="Display name"
                name="display_name"
                autoComplete="nickname"
                maxLength={100}
                disabled={busy}
                hint="What colleagues see on envelopes you send."
                value={displayNameEdited ? displayName : suggestedDisplayName(firstName, lastName)}
                onChange={(e) => {
                  setDisplayNameEdited(true)
                  setDisplayName(e.currentTarget.value)
                }}
              />
              <Field
                label="Password"
                type="password"
                name="password"
                autoComplete="new-password"
                required
                disabled={busy}
                hint={PASSWORD_HINT}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                {...(passwordError ? { error: passwordError } : {})}
              />
              {failure?.field === 'form' ? (
                <Callout tone="danger" title="That did not work">
                  {failure.message}
                </Callout>
              ) : null}
              <Button
                type="submit"
                disabled={busy || tooShort || firstName.trim() === '' || lastName.trim() === ''}
                className="w-full"
              >
                {busy ? 'Joining…' : `Create account and join`}
              </Button>
              <p className="text-text-muted m-0 text-sm">
                Already have a FlowZa account?{' '}
                <button
                  type="button"
                  className="text-accent font-medium underline"
                  onClick={() => setMode('signin')}
                >
                  Sign in to accept
                </button>
              </p>
            </form>
          ) : (
            <form
              className="mt-4 flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault()
                onSignIn(password)
              }}
            >
              <Field
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                disabled={busy}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                {...(passwordError ? { error: passwordError } : {})}
              />
              {failure?.field === 'form' ? (
                <Callout tone="danger" title="That did not work">
                  {failure.message}
                </Callout>
              ) : null}
              <Button type="submit" disabled={busy || password === ''} className="w-full">
                {busy ? 'Joining…' : 'Sign in and join'}
              </Button>
              <p className="text-text-muted m-0 text-sm">
                No account yet?{' '}
                <button
                  type="button"
                  className="text-accent font-medium underline"
                  onClick={() => setMode('create')}
                >
                  Create one
                </button>
              </p>
            </form>
          )}
        </>
      )}
    </Shell>
  )
}

function Shell({
  title,
  subtitle,
  children,
}: {
  readonly title: string
  readonly subtitle?: React.ReactNode
  readonly children: React.ReactNode
}) {
  return (
    <AuthLayout title={title} {...(subtitle === undefined ? {} : { subtitle })}>
      <Card>{children}</Card>
    </AuthLayout>
  )
}

export default function Join() {
  const [token, setToken] = useState<string | null | undefined>(undefined)
  const [state, setState] = useState<JoinState>({ kind: 'reading' })
  const [failure, setFailure] = useState<
    { readonly field: 'password' | 'form'; readonly message: string } | undefined
  >(undefined)
  /**
   * The two retryable steps, held so a Try-again button can call the CURRENT one.
   *
   * Refs rather than a direct self-reference: `retry: () => load(found)` inside `load` reads the
   * binding before it is declared, which the compiler lint refuses for a real reason — the closure
   * would keep whichever version of the function existed when the error state was built, and stop
   * following the one that re-renders. Assigned in an effect below, never during a render.
   */
  const loadRef = useRef<((found: string | null) => Promise<void>) | null>(null)
  const finishRef = useRef<
    | ((
        names: { firstName: string; lastName: string; displayName: string } | null,
        organizationName: string,
      ) => Promise<void>)
    | null
  >(null)

  const load = useCallback(async (found: string | null) => {
    if (found === null) {
      setState({ kind: 'no_token' })
      return
    }
    setState({ kind: 'reading' })
    try {
      const invitation = await previewInvitation(found)
      if (invitation === null) {
        setState({ kind: 'not_open' })
        return
      }
      // This browser's own session, if it has one. Read AFTER the preview so a signed-in visitor
      // sees the invitation they are accepting rather than a bare button.
      const client = getClient()
      const session = client === null ? null : (await client.auth.getSession()).data.session
      setState({
        kind: 'ready',
        invitation,
        signedInAs: session?.user.email ?? null,
      })
    } catch (error) {
      // A 404 here is NOT "this invitation is gone" — `previewInvitation` already turned that into
      // null above. It means the deployed Worker predates the preview route, which is a deployment
      // fact and not something the person holding the link can act on, so it is said as one.
      setState({
        kind: 'error',
        message: describeUnlessNotDeployed(
          error,
          'Joining by invitation',
          'The invitation could not be read just now.',
        ),
        retry: () => void loadRef.current?.(found),
      })
    }
  }, [])

  useEffect(() => {
    loadRef.current = load
  }, [load])

  useEffect(() => {
    const found = takeTokenFromFragment(window.location)
    setToken(found)
    // Cleared whether or not a token was found, and with `replaceState` so the address bar does not
    // keep it and the back button does not restore it.
    if (window.location.hash.length > 0) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    void load(found)
  }, [load])

  /**
   * Redeem, refresh, and record the name — in that order, and the order is the whole of it.
   *
   * Claims are MINTED at refresh rather than patched into the token in hand, so the organization
   * exists but this session cannot see it until `refreshSession` runs. The profile write needs an
   * org-scoped session, which is why it is last, and why a failure there does not undo the join:
   * the person is in the organization and can set their name in Settings, so losing the membership
   * to save a display name would be the worse trade.
   */
  const finish = useCallback(
    async (
      names: { firstName: string; lastName: string; displayName: string } | null,
      // Passed in rather than read back off `state`, which by this point every caller has already
      // replaced with `working` — reading it here answered "your organization" on the one screen
      // whose whole job is to name the organization somebody just joined.
      organizationName: string,
    ) => {
      if (typeof token !== 'string') return
      setState({ kind: 'working', note: 'Joining the organization' })
      try {
        await api('/v1/organization/bootstrap', {
          method: 'POST',
          body: { invitation_token: token },
        })
      } catch (error) {
        if (error instanceof ApiError && error.code === 'invitation_not_open') {
          setState({ kind: 'refused', message: error.message })
          return
        }
        if (error instanceof ApiError && error.code === 'already_provisioned') {
          setState({
            kind: 'refused',
            message: 'your account already belongs to a FlowZa organization',
          })
          return
        }
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'joining did not work',
          retry: () => void finishRef.current?.(names, organizationName),
        })
        return
      }

      const client = getClient()
      if (client !== null) await client.auth.refreshSession()

      if (names !== null) {
        try {
          await api('/v1/profile', {
            method: 'PATCH',
            body: {
              display_name: names.displayName === '' ? null : names.displayName,
              first_name: names.firstName === '' ? null : names.firstName,
              last_name: names.lastName === '' ? null : names.lastName,
            },
          })
        } catch {
          // Deliberately swallowed: the membership is the thing that was hard to get, and Settings
          // is one click from where this lands. A name that did not save is a nuisance; a join
          // undone because of it would be a defect.
        }
      }

      setState({ kind: 'joined', organizationName })
      // A full load rather than a router navigation: the session gained its org claims a moment
      // ago, and every provider above this reads them once at mount.
      window.location.assign('/app')
    },
    [token],
  )

  useEffect(() => {
    finishRef.current = finish
  }, [finish])

  const onCreate = useCallback(
    (input: JoinSubmission) => {
      void (async () => {
        if (state.kind !== 'ready') return
        const client = getClient()
        if (client === null) {
          setFailure({ field: 'form', message: 'this deployment is not configured for sign-up' })
          return
        }
        setFailure(undefined)
        const email = state.invitation.email
        const invitation = state.invitation
        const signedInAs = state.signedInAs
        // Stashed BEFORE the signup, for the one path this screen cannot finish itself: an address
        // the cell wants confirmed. `ProvisionOrganization` reads and removes it, so somebody who
        // confirms in this same browser session and lands on /app still joins rather than being
        // given a personal organization. Session storage, not local: it is a live credential, and
        // the tab that was invited is the only one that should hold it.
        try {
          window.sessionStorage.setItem('flowza.invitation_token', token ?? '')
        } catch {
          // A browser refusing storage costs one more click on the original link. Not a failure.
        }
        setState({ kind: 'working', note: 'Creating your account' })
        const { data, error } = await client.auth.signUp({
          email,
          password: input.password,
          options: {
            data: {
              ...(CELL_REGION === undefined ? {} : { region: CELL_REGION }),
              first_name: input.firstName,
              last_name: input.lastName,
              display_name: input.displayName,
            },
          },
        })
        if (error) {
          const weak = weakPasswordMessage(error)
          setFailure(
            weak === null
              ? {
                  field: 'form',
                  message:
                    'We could not create an account with those details. Check them and try again.',
                }
              : { field: 'password', message: weak },
          )
          setState({ kind: 'ready', invitation, signedInAs })
          return
        }
        if (data.session === null) {
          // No session and no error. Either the cell wants the address confirmed, or that address
          // already has an account and Supabase is refusing to say so — the screen says both.
          setState({ kind: 'confirm', email })
          return
        }
        await finish(
          {
            firstName: input.firstName,
            lastName: input.lastName,
            displayName: input.displayName,
          },
          invitation.organizationName,
        )
      })()
    },
    [finish, state, token],
  )

  const onSignIn = useCallback(
    (password: string) => {
      void (async () => {
        if (state.kind !== 'ready') return
        const client = getClient()
        if (client === null) {
          setFailure({ field: 'form', message: 'this deployment is not configured for sign-in' })
          return
        }
        setFailure(undefined)
        const invitation = state.invitation
        const signedInAs = state.signedInAs
        setState({ kind: 'working', note: 'Signing you in' })
        const { error } = await client.auth.signInWithPassword({
          email: invitation.email,
          password,
        })
        if (error) {
          const code = (error as { code?: string }).code
          setFailure({
            field: 'form',
            message:
              code === 'email_not_confirmed'
                ? 'That address has not been confirmed yet. Open the confirmation email first, then come back to this invitation.'
                : 'That password did not work for this address.',
          })
          setState({ kind: 'ready', invitation, signedInAs })
          return
        }
        await finish(null, invitation.organizationName)
      })()
    },
    [finish, state],
  )

  const onAcceptAsSignedIn = useCallback(() => {
    if (state.kind !== 'ready') return
    void finish(null, state.invitation.organizationName)
  }, [finish, state])

  const onSignOut = useCallback(() => {
    void (async () => {
      await getClient()?.auth.signOut()
      window.location.reload()
    })()
  }, [])

  if (token === undefined && state.kind === 'reading') {
    return <LoadingState label="Reading your invitation" />
  }

  return (
    <JoinView
      state={state}
      onCreate={onCreate}
      onSignIn={onSignIn}
      onAcceptAsSignedIn={onAcceptAsSignedIn}
      onSignOut={onSignOut}
      {...(failure === undefined ? {} : { failure })}
    />
  )
}
